import { useMemo } from 'react'
import cx from 'classnames'
import { Button } from 'blaze-ui/Button'
import { AlertTriangle } from 'blaze-ui/icons/20/AlertTriangle'
import { AlmanacFileShowItem } from 'hooks/queries/files/types'
import { useIntegrationHeartbeatStatus } from 'hooks/useIntegrationHeartbeatStatus'
import { ScheduledPostTarget } from 'types'
import { derivePreviewAlert } from './previewAlertBannerConfigs'
import Styles from './PreviewAlertBanner.module.scss'

interface PreviewAlertBannerProps {
  almanacFile?: AlmanacFileShowItem
  onRefreshConnection?: (target: ScheduledPostTarget) => void
  onAdjustCaption?: (target: ScheduledPostTarget) => void
}

export const PreviewAlertBanner = ({ almanacFile, onRefreshConnection, onAdjustCaption }: PreviewAlertBannerProps) => {
  const { disconnectedIntegrations } = useIntegrationHeartbeatStatus()

  const alert = useMemo(
    () => (almanacFile ? derivePreviewAlert({ almanacFile, disconnectedIntegrations }) : null),
    [almanacFile, disconnectedIntegrations],
  )

  if (!almanacFile || !alert) return null

  const handleAction = () => {
    if (!alert.target) return
    if (alert.action === 'refresh-connection') {
      onRefreshConnection?.(alert.target)
    } else if (alert.action === 'adjust-caption') {
      onAdjustCaption?.(alert.target)
    }
  }

  return (
    <div
      role="alert"
      data-testid="preview-alert-banner"
      data-alert-kind={alert.testId}
      className={cx(Styles.banner, alert.variant === 'error' ? Styles.error : Styles.warning)}
    >
      <AlertTriangle size={20} />
      <span className={Styles.message}>{alert.message}</span>
      <Button variant="secondary" onPress={handleAction}>
        {alert.actionLabel}
      </Button>
    </div>
  )
}
